import './activationPinVerifyPage.scss';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import { ThemeProvider } from '@mui/material/styles';
import { AuthType } from '@thesavingsgroup/auth/AuthType';
import isEmpty from 'lodash/isEmpty';
import uniqueId from 'lodash/uniqueId';
import { FormEvent, ReactNode, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import vehicleSUVSideView from '../../assets/vehicle-suv-sideview-one.png';
import { Header } from '../../components/Header/Header';
import { OTP } from '../../components/OTP/OTP';
import { inputEventHandler } from '../../components/utils/inputEventHandler';
import useApiStore from '../../store/apiStore/useApiStore';
import useParameterStore from '../../store/parameterStore/useParameterStore';
import { DeliverPinLocation } from '../../store/pinStore/enums/DeliverPinLocation';
import { useCountdownTimer } from '../../store/pinStore/hooks/useCountdownTimer';
import usePinStore from '../../store/pinStore/usePinStore';
import useSignOutStore from '../../store/signOutStore/useSignOutStore';
import useThemeStore from '../../store/themeStore/useThemeStore';
import useUserStore from '../../store/userStore/useUserStore';
import { locator } from '../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { isLoading } = useApiStore();
  const {
    clearPin,
    deliverPin,
    deliverPinFailedMessage,
    deliverPinRetrySuccess,
    pin,
    primaryPhoneNumber,
    setPin,
    computed: { hasValidPin },
  } = usePinStore();
  const {
    clearPinRetryState,
    userAgreeToTerms,
    userAuthenticate,
    userAuthenticateFailedMessage,
    userAuthenticateLocked,
  } = useUserStore();
  const { signOut } = useSignOutStore();
  const {
    authType,
    collectAgreeToTerms,
    coApplicantCollectAgreeToTerms,
    leadChannelCode,
    vehicleId,
  } = useParameterStore();

  const resendTimerDuration = 15; // in seconds
  const [seconds, setSecondsLeft] = useState(0);
  const { startTimer, stopTimer } = useCountdownTimer(
    setSecondsLeft,
    resendTimerDuration,
  );

  const shouldAgreeToTerms =
    collectAgreeToTerms === 'true' || coApplicantCollectAgreeToTerms === 'true';

  const buttonText = useMemo((): string => {
    if (seconds > 0) {
      return t('ActivationPinVerifyPage.resendCodeDelay', {
        delay: seconds,
      });
    }
    return t('ActivationPinVerifyPage.resendCode');
  }, [t, seconds]);

  const formSubmit = async (event?: FormEvent): Promise<void> => {
    event?.preventDefault();
    if (primaryPhoneNumber && pin) {
      await userAuthenticate(
        primaryPhoneNumber,
        pin,
        leadChannelCode,
        authType || AuthType.ACCOUNT_ACTIVATION_USING_PIN,
      );
      const { userAuthenticateSuccess } = useUserStore.getState();
      if (userAuthenticateSuccess && shouldAgreeToTerms) {
        await userAgreeToTerms(vehicleId);
      }
    }
  };

  const resendCode = async (): Promise<void> => {
    if (primaryPhoneNumber) {
      startTimer();
      clearPinRetryState();
      await deliverPin({
        deliverPinLocation: DeliverPinLocation.RETRY,
        phone: primaryPhoneNumber,
        purpose: authType || AuthType.ACCOUNT_ACTIVATION_USING_PIN,
      });
      clearPin();
    }
  };

  useEffect((): (() => void) => {
    if (!primaryPhoneNumber) {
      navigate('/activation');
    }
    if (userAuthenticateLocked) {
      signOut({
        withMessage: userAuthenticateFailedMessage,
      });
    }
    startTimer();
    return (): void => {
      stopTimer();
    };
  }, [primaryPhoneNumber, userAuthenticateLocked]);

  useEffect((): void => {
    if (userAuthenticateFailedMessage) {
      clearPin();
    }
  }, [userAuthenticateFailedMessage]);

  useEffect((): (() => void) => {
    // Clearing pin on unmount so the next pin page starts fresh
    return (): void => {
      clearPin();
    };
  }, []);

  useEffect((): void => {
    // Auto-submit once the last digit is entered
    if (hasValidPin) {
      formSubmit();
    }
  }, [hasValidPin]);

  return (
    <div className="activation-pin-verify-page">
      <ThemeProvider theme={theme}>
        <Box sx={theme.pageLayout}>
          {deliverPinRetrySuccess && (
            <Snackbar
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              autoHideDuration={5000}
              open
              onClose={(): void => {
                usePinStore.setState({ deliverPinRetrySuccess: false });
              }}
            >
              <Alert
                data-testid={locator.pages.activationPinVerify.alert.success}
                icon={<CheckCircleIcon />}
                severity="success"
              >
                {t('ActivationPinVerifyPage.codeSent')}
              </Alert>
            </Snackbar>
          )}

          {!isEmpty(deliverPinFailedMessage) && (
            <Snackbar
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              autoHideDuration={5000}
              open
              onClose={(): void => {
                usePinStore.setState({ deliverPinFailedMessage: null });
              }}
            >
              <Alert
                data-testid={locator.pages.activationPinVerify.alert.error}
                severity="error"
              >
                {deliverPinFailedMessage}
              </Alert>
            </Snackbar>
          )}

          <Header showLogo />

          <main>
            <figure>
              <img
                alt={t('imageAlts.vehicleSUVSideView')}
                src={vehicleSUVSideView}
              />
            </figure>
            <form
              data-testid={locator.pages.activationPinVerify.form.root}
              noValidate
              onSubmit={formSubmit}
            >
              <div className="form-content">
                <h1 data-testid={locator.pages.activationPinVerify.form.h1}>
                  {t('ActivationPinVerifyPage.title')}
                </h1>
                <h3 data-testid={locator.pages.activationPinVerify.form.h3}>
                  <Trans
                    components={[
                      <span
                        key={uniqueId('span-')}
                        style={{ textDecoration: 'underline' }}
                      />,
                    ]}
                    i18nKey={'ActivationPinVerifyPage.subtitle'}
                    values={{
                      phoneNumber:
                        inputEventHandler.phoneNumber.formatter(
                          primaryPhoneNumber,
                        ),
                    }}
                  />
                </h3>

                <OTP
                  disabled={isLoading}
                  error={userAuthenticateFailedMessage}
                  errorTestId={
                    locator.pages.activationPinVerify.form.pinInputError
                  }
                  inputTestId={locator.pages.activationPinVerify.form.pinInput}
                  value={pin}
                  onChange={setPin}
                />

                <Button
                  color="primary"
                  data-testid={
                    locator.pages.activationPinVerify.form.resendPinLink
                  }
                  disabled={seconds > 0}
                  loading={isLoading}
                  type="button"
                  variant="contained"
                  fullWidth
                  onClick={resendCode}
                >
                  {buttonText}
                </Button>
              </div>
              <footer
                data-testid={locator.pages.activationPinVerify.footer.root}
              >
                {t('copyright', { year: new Date().getFullYear() })}
              </footer>
            </form>
          </main>
        </Box>
      </ThemeProvider>
    </div>
  );
}

Component.displayName = 'ActivationPinVerifyPage';
